import { createContext, useContext, useCallback, useEffect, useState } from 'react'
import { supabase, isConfigured } from '../lib/supabase.js'
import { useAuth } from './AuthContext.jsx'
import { PEPTIDES as STATIC_PEPTIDES } from '../data/peptides.js'

const PeptidesContext = createContext(null)

/* ---------- row <-> app-shape mapping ---------- */
const rowToPeptide = (r) => ({
  id: r.id,
  slug: r.slug,
  name: r.name,
  type: r.type || 'peptide',
  category: r.category,
  summary: r.summary,
  aka: r.aka || [],
  tags: r.tags || [],
  sequence: r.sequence,
  overview: r.overview,
  mechanism: r.mechanism || [],
  researchedUses: r.researched_uses || [],
  protocol: r.protocol || {},
  halfLife: r.half_life,
  reconstitution: r.reconstitution,
  cautions: r.cautions || [],
  calcPreset: r.calc_preset || null,
  sortOrder: r.sort_order ?? 100,
})

const peptideToRow = (p, order) => ({
  slug: p.slug,
  name: p.name,
  type: p.type || 'peptide',
  category: p.category ?? null,
  summary: p.summary ?? null,
  aka: p.aka || [],
  tags: p.tags || [],
  sequence: p.sequence ?? null,
  overview: p.overview ?? null,
  mechanism: p.mechanism || [],
  researched_uses: p.researchedUses || [],
  protocol: p.protocol || {},
  half_life: p.halfLife ?? null,
  reconstitution: p.reconstitution ?? null,
  cautions: p.cautions || [],
  calc_preset: p.calcPreset || null,
  sort_order: order ?? p.sortOrder ?? 100,
  updated_at: new Date().toISOString(),
})

// Static profiles used as (a) the pre-setup fallback and (b) the seed source.
const staticList = STATIC_PEPTIDES.map((p, i) => ({ ...p, sortOrder: i * 10 }))

export function PeptidesProvider({ children }) {
  const { user } = useAuth()
  const [peptides, setPeptides] = useState(staticList)
  // Start from the bundled profiles so pages render real content immediately
  // (crucial for prerendering/SEO); the DB load swaps in over it on the client.
  const [source, setSource] = useState('static')
  const [isAdmin, setIsAdmin] = useState(false)

  const load = useCallback(async () => {
    if (!isConfigured) {
      setPeptides(staticList)
      setSource('static')
      return
    }
    const { data, error } = await supabase
      .from('peptides')
      .select('*')
      .order('sort_order', { ascending: true })
    if (error || !data || data.length === 0) {
      // Table not created yet, empty, or unreachable — fall back to bundled
      // content so public pages are never blank.
      setPeptides(staticList)
      setSource(error ? 'static' : 'empty')
    } else {
      setPeptides(data.map(rowToPeptide))
      setSource('db')
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // Determine admin status for the signed-in user.
  useEffect(() => {
    if (!isConfigured || !user) {
      setIsAdmin(false)
      return
    }
    let active = true
    supabase
      .from('admins')
      .select('user_id')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => active && setIsAdmin(!!data))
    return () => {
      active = false
    }
  }, [user])

  /* ---------- admin CRUD ---------- */
  const createPeptide = useCallback(async (p) => {
    const { data, error } = await supabase.from('peptides').insert(peptideToRow(p)).select().single()
    if (error) return { error: error.message }
    setPeptides((list) => [...list, rowToPeptide(data)].sort((a, b) => a.sortOrder - b.sortOrder))
    setSource('db')
    return { record: rowToPeptide(data) }
  }, [])

  const updatePeptide = useCallback(async (id, p) => {
    const { data, error } = await supabase.from('peptides').update(peptideToRow(p)).eq('id', id).select().single()
    if (error) return { error: error.message }
    const rec = rowToPeptide(data)
    setPeptides((list) => list.map((x) => (x.id === id ? rec : x)).sort((a, b) => a.sortOrder - b.sortOrder))
    return { record: rec }
  }, [])

  const deletePeptide = useCallback(async (id) => {
    const { error } = await supabase.from('peptides').delete().eq('id', id)
    if (error) return { error: error.message }
    setPeptides((list) => list.filter((x) => x.id !== id))
    return {}
  }, [])

  // Which bundled starter profiles aren't in the database yet.
  // (When the table is empty we're showing the static fallback, so treat all as missing.)
  const dbSlugs = source === 'db' ? new Set(peptides.map((p) => p.slug)) : new Set()
  const missingStarters = staticList.filter((p) => !dbSlugs.has(p.slug))

  // Seed the database with any bundled starter profiles it's missing. Safe to
  // run repeatedly — it only inserts what isn't already there.
  const seedStarter = useCallback(async () => {
    const missing = staticList.filter((p) => !(source === 'db' ? new Set(peptides.map((x) => x.slug)) : new Set()).has(p.slug))
    if (missing.length === 0) return { count: 0 }
    const base = source === 'db' ? peptides.length : 0
    const rows = missing.map((p, i) => peptideToRow(p, (base + i) * 10))
    const { error } = await supabase.from('peptides').insert(rows)
    if (error) return { error: error.message }
    await load()
    return { count: rows.length }
  }, [peptides, source, load])

  const getBySlug = useCallback((slug) => peptides.find((p) => p.slug === slug), [peptides])

  const value = {
    peptides,
    source, // 'loading' | 'db' | 'empty' | 'static'
    loading: source === 'loading',
    isAdmin,
    getBySlug,
    createPeptide,
    updatePeptide,
    deletePeptide,
    seedStarter,
    missingStarters: missingStarters.length,
    reload: load,
  }
  return <PeptidesContext.Provider value={value}>{children}</PeptidesContext.Provider>
}

export const usePeptides = () => useContext(PeptidesContext)
